import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { EnvService } from '@lib/env';
import { IimMultiselectPreferences } from '@iim-angular/components';
import { ITransmissionValue } from '@lib/client';
import { TransmissionSupplierBasket, UpdateContactPersonsRequest } from '../model/basket.model';
import {
    ContactPerson,
    ContactPersonOption,
    SupplierBasket
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';

@Component({
    selector: 'basket-address-item',
    templateUrl: './basket-address-item.component.html',
    standalone: false
})
export class BasketAddressItemComponent implements OnInit, OnChanges {
    @Input() supplierBasket!: SupplierBasket;
    @Input() isLast!: boolean;
    @Input() isEditableStep!: boolean;

    @Output() contactPersonsUpdated = new EventEmitter<UpdateContactPersonsRequest>();
    @Output() transmissionInfoUpdated = new EventEmitter<TransmissionSupplierBasket>();

    env = EnvService;
    contacts: ContactPersonOption[] = [];
    /** Selected contacts that are the standard: the multiselect chips cannot show the star themselves. */
    selectedStandardContacts: ContactPersonOption[] = [];

    preferences!: IimMultiselectPreferences;
    removeLastValueOnBackspace = true;
    disabled = false;
    showSelectAll = false;
    required = true;
    requiredLength = 1;

    ngOnInit(): void {
        this.constructPreferences();
    }

    // Runs on every supplierBasket change including the first binding. Since the contact dropdown is
    // loaded asynchronously and the store now updates it immutably (UC_BA_1021), ngOnChanges fires once
    // the dropdown arrives — rebuilding the multiselect options and applying the single-contact
    // auto-select, so the field fills in on first entry instead of staying empty until a manual reload.
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['supplierBasket'] && this.supplierBasket) {
            this.mapContacts();
            this.getSupplierContactPerson();
            this.updateContactPersons();
        }
    }

    mapContacts(): void {
        const standardContactIds = new Set(
            (this.supplierBasket.supplierContactsDropdown ?? [])
                .filter((entry) => entry.isStandard)
                .map((entry) => entry.contact.realSupplierContactId),
        );
        if (this.supplierBasket.supplierContacts) {
            this.supplierBasket.supplierContacts = this.supplierBasket?.supplierContacts.map(
                (supplierContact) =>
                    new ContactPersonOption(
                        {
                            realSupplierContactId: supplierContact.realSupplierContactId,
                            firstName: supplierContact.firstName,
                            surName: supplierContact.surName,
                            title: supplierContact.title,
                            email: supplierContact.email,
                        } as ContactPerson,
                        standardContactIds.has(supplierContact.realSupplierContactId),
                    ),
            );
        }
        this.selectedStandardContacts = (this.supplierBasket.supplierContacts ?? []).filter(
            (contact) => contact.isStandard,
        );
    }

    getSupplierContactPerson(): ContactPersonOption[] {
        // Rebuild (not append) so repeated ngOnChanges passes never duplicate the options.
        this.contacts = (this.supplierBasket.supplierContactsDropdown ?? []).map(
            (contactPerson) =>
                new ContactPersonOption(
                    {
                        realSupplierContactId: contactPerson.contact.realSupplierContactId,
                        firstName: contactPerson.contact.firstName,
                        surName: contactPerson.contact.surName,
                        email: contactPerson.email,
                        title: contactPerson.contact.title,
                    } as ContactPerson,
                    contactPerson.isStandard === true,
                ),
        );

        return this.contacts;
    }

    updateContactPersonsWithMapping(contactPersons: ContactPersonOption[], supplierBasketId: string) {
        this.contactPersonsUpdated.emit({
            contactPersons: contactPersons.map((x) => {
                return {
                    realSupplierContactId: x.realSupplierContactId,
                    firstName: x.firstName,
                    surName: x.surName,
                    email: x.email,
                    title: x.title,
                } as ContactPerson;
            }),
            supplierBasketId,
        });
    }

    constructPreferences(): void {
        this.preferences = {
            removeLastValueOnBackspace: this.removeLastValueOnBackspace,
            showSelectAll: this.showSelectAll,
        };
    }

    updateTransmissionInfo(transmissionInfo: ITransmissionValue, supplierBasketId: any): void {
        this.transmissionInfoUpdated.emit({ transmissionInfo, supplierBasketId });
    }

    private updateContactPersons() {
        // Auto-select the single available contact. Deliberately re-attempts on every supplierBasket
        // change while the selection is still empty (self-healing after a failed save or a basket
        // reload): the POST idempotently replaces the selection, and once it succeeded the refreshed
        // state satisfies contactExist(), which stops further emits. A failed POST produces no state
        // change, so this cannot loop — the store surfaces the error via notification instead.
        if (this.supplierBasket.supplierContactsDropdown?.length === 1 && !this.contactExist()) {
            const contactPerson = this.supplierBasket.supplierContactsDropdown[0];
            this.contactPersonsUpdated.emit({
                contactPersons: [
                    {
                        realSupplierContactId: contactPerson.contact.realSupplierContactId,
                        firstName: contactPerson.contact.firstName,
                        surName: contactPerson.contact.surName,
                        email: contactPerson.email,
                        title: contactPerson.contact.title,
                    } as ContactPerson,
                ],
                supplierBasketId: this.supplierBasket.id,
            });
        }
    }

    private contactExist(): boolean {
        return this.supplierBasket?.supplierContacts !== undefined && this.supplierBasket?.supplierContacts.length > 0;
    }
}
